import { useEffect, useRef } from 'react';
import { DataSet } from 'vis-data/peer';
import { Network } from 'vis-network/peer';

import type {
  GraphEntity,
  GraphRelationship,
  GraphSelection,
} from './world-model.types';

// Labels are hidden below this zoom scale for a clean dot-graph view when
// fully zoomed out — they reappear and scale naturally when zooming in.
const LABEL_VISIBLE_SCALE_MIN = 0.2;
const LABEL_BASE_CANVAS_PX = 11;
const LABEL_FLOOR_SCREEN_PX = 8;
const LABEL_CEILING_SCREEN_PX = 16;

function toTitleCase(text: string): string {
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function GraphCanvas({
  entities,
  relationships,
  colorMap,
  matchedIds,
  onSelect,
}: {
  entities: GraphEntity[];
  relationships: GraphRelationship[];
  colorMap: Record<string, string>;
  matchedIds: Set<string> | null;
  onSelect: (selection: GraphSelection) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const networkRef = useRef<Network | null>(null);
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const entityById = new Map(entities.map((e) => [e.id, e]));
    const relById = new Map(relationships.map((r) => [r.id, r]));

    const nodeData = new DataSet(
      entities.map((e) => {
        const color = colorMap[e.type] ?? '#9CA3AF';
        const isMatch = matchedIds?.has(e.id) ?? false;
        return {
          id: e.id,
          label: toTitleCase(e.name ?? e.type),
          color: {
            background: color,
            border: isMatch ? 'rgba(255,255,255,0.75)' : color,
            highlight: {
              background: color,
              border: isMatch ? '#ffffff' : color,
            },
          },
          borderWidth: isMatch ? 2.5 : 1,
          shape: 'dot' as const,
          size: isMatch ? 13 : 10,
          // Hardcoded muted color — CSS variables don't work inside canvas rendering
          font: { color: '#94a3b8', size: LABEL_BASE_CANVAS_PX },
        };
      })
    );

    const edges = relationships.map((r) => ({
      id: r.id,
      from: r.source,
      to: r.target,
      // Very faint so they don't dominate the canvas
      color: {
        color: 'rgba(148,163,184,0.15)',
        highlight: 'rgba(148,163,184,0.5)',
        hover: 'rgba(148,163,184,0.4)',
      },
      width: 1,
      arrows: { to: { enabled: true, scaleFactor: 0.3 } },
      title: r.semantic ?? undefined,
      smooth: { enabled: true, type: 'dynamic' as const, roundness: 0.5 },
    }));

    const network = new Network(
      containerRef.current,
      { nodes: nodeData, edges },
      {
        physics: {
          enabled: true,
          solver: 'forceAtlas2Based',
          forceAtlas2Based: {
            gravitationalConstant: -50,
            centralGravity: 0.01,
            springLength: 200,
            springConstant: 0.05,
            damping: 0.95,
            avoidOverlap: 1.0,
          },
          stabilization: { enabled: true, iterations: 150, fit: true },
        },
        interaction: { hover: true, tooltipDelay: 200 },
        edges: { selectionWidth: 2 },
      }
    );

    // Below LABEL_VISIBLE_SCALE_MIN the graph is too zoomed out to read labels —
    // hide them entirely for a clean dot view. Above it, scale labels naturally
    // within a floor/ceiling so they stay readable without ballooning.
    const syncLabelSizes = () => {
      const scale = network.getScale();
      if (scale < LABEL_VISIBLE_SCALE_MIN) {
        nodeData.update(entities.map((e) => ({ id: e.id, label: '' })));
        return;
      }
      const naturalScreenPx = LABEL_BASE_CANVAS_PX * scale;
      const clampedScreenPx = Math.max(
        LABEL_FLOOR_SCREEN_PX,
        Math.min(LABEL_CEILING_SCREEN_PX, naturalScreenPx)
      );
      const canvasFontSize = Math.round(clampedScreenPx / scale);
      nodeData.update(
        entities.map((e) => ({
          id: e.id,
          label: toTitleCase(e.name ?? e.type),
          font: { color: '#94a3b8', size: canvasFontSize },
        }))
      );
    };

    network.on('zoom', syncLabelSizes);
    network.on('stabilized', syncLabelSizes);

    network.on('click', (params) => {
      if (params.nodes.length > 0) {
        const entity = entityById.get(String(params.nodes[0]));
        if (entity) {
          onSelectRef.current({
            kind: 'node',
            entity,
            color: colorMap[entity.type] ?? '#9CA3AF',
          });
        }
      } else if (params.edges.length > 0) {
        const rel = relById.get(String(params.edges[0]));
        if (rel) {
          onSelectRef.current({ kind: 'edge', rel });
        }
      } else {
        onSelectRef.current(null);
      }
    });

    networkRef.current = network;
    return () => {
      network.destroy();
      networkRef.current = null;
    };
    // Re-initialize only when graph data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, relationships]);

  return <div ref={containerRef} className="w-full h-full" />;
}
